import { call, put, takeLatest } from "redux-saga/effects";
import {
  VERIFY_OTP_REQUEST,
  VERIFY_OTP_SUCCESS,
  VERIFY_OTP_FAILURE,
  RESEND_OTP_REQUEST,
  RESEND_OTP_SUCCESS,
} from "../../actionTypes/otpValidation";
import { doVerifyOtp, resendOtp } from "../../../com/otpValidation";
import { BaseResponse } from "../../response";
import { API_CODES } from "../../../constants";
import { storeAuthData } from "../../../utils/auth/authStorage";
import { getUserDetailsByEmail } from "../../../api/services/userManagement.service";
import { isUserDetailsSuccess } from "../../../api/types/userManagement.types";

/** localStorage key for user details (stored after verify-OTP success) */
const USER_DETAILS_STORAGE_KEY = "linn-i-am-userDetails";

interface OtpValidationAction {
  type: typeof VERIFY_OTP_REQUEST;
  payload: {
    otpCode: string;
    email: string;
    onSuccess?: () => void;
  };
}

interface ResendOtpValidationAction {
  type: typeof RESEND_OTP_REQUEST;
  payload: {
    emailOrPhone: string;
  };
}

interface ErrorResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

export function* handleVerifyOtp(
  action: OtpValidationAction,
): Generator<unknown, void, BaseResponse> {
  const { otpCode, email } = action.payload;

  try {
    const response: BaseResponse = yield call(doVerifyOtp, {
      otpCode,
      email,
    });

    console.log("🟡 [verify-login-otp] raw response:\n" + JSON.stringify(response, null, 2));

    if (response.status === API_CODES.FAILURE_CODE || response.status === "FAILURE") {
      yield put({
        type: VERIFY_OTP_FAILURE,
        payload: response.statusinfo.message || "Invalid OTP",
      });
    } else {
      // Extract auth data from response (tokens and user info)
      // Response structure: { status, statusinfo, data: { accessToken, refreshToken, user } }
      const responseData = response.data || response;

      if (responseData?.tenantSelectionRequired) {
        yield put({
          type: VERIFY_OTP_SUCCESS,
          payload: response,
        });
        return;
      }
      
      console.log("🟢 handleVerifyOtp: OTP verified successfully", {
        hasAccessToken: !!responseData?.accessToken,
        hasRefreshToken: !!responseData?.refreshToken,
        hasUser: !!responseData?.user,
        responseDataKeys: responseData ? Object.keys(responseData) : [],
      });

      // Store auth data using unified storage method
      // Store in Redux store (persisted to persist:userdb)
      if (responseData?.accessToken || responseData?.refreshToken) {
        try {
          storeAuthData({
            accessToken: responseData.accessToken || "",
            refreshToken: responseData.refreshToken || "",
            sessionId: responseData.sessionId || "",
            activeRole: responseData.activeRole,
            availableRoles: responseData.availableRoles,
            user: responseData.user || {
              firstname: responseData.firstname || "",
              lastname: responseData.lastname || "",
            },
          });
        } catch (error) {
          console.error("🔴 handleVerifyOtp: Error storing auth data", error);
        }
      } else {
        console.warn("🔴 handleVerifyOtp: No tokens found in response", {
          responseData,
          response,
        });
      }

      // Call userDetails with email and store full API payload in localStorage
      // (includes accountDetails for tenant display across all micro-apps).
      if (email) {
        try {
          const userDetailsResponse = yield call(getUserDetailsByEmail, email);
          if (isUserDetailsSuccess(userDetailsResponse as any)) {
            const data = (userDetailsResponse as { data?: Record<string, unknown> }).data;
            const usersDetails = data?.usersDetails;
            // If API returns accountDetails at data-level, mirror onto the first user entry
            // so consumers reading usersDetails[0].accountDetails always find it.
            if (
              data &&
              Array.isArray(usersDetails) &&
              usersDetails.length > 0 &&
              data.accountDetails &&
              typeof usersDetails[0] === "object" &&
              usersDetails[0] !== null &&
              !(usersDetails[0] as Record<string, unknown>).accountDetails
            ) {
              (usersDetails[0] as Record<string, unknown>).accountDetails = data.accountDetails;
            }
            const payload = JSON.stringify(userDetailsResponse);
            if (typeof localStorage !== "undefined") {
              localStorage.setItem(USER_DETAILS_STORAGE_KEY, payload);
              console.log("🟢 handleVerifyOtp: Stored userDetails in localStorage", {
                email,
                hasAccountDetails: !!(
                  (Array.isArray(usersDetails) &&
                    usersDetails[0] &&
                    (usersDetails[0] as Record<string, unknown>).accountDetails) ||
                  data?.accountDetails
                ),
              });
            }
          }
        } catch (userDetailsError) {
          console.warn("🔴 handleVerifyOtp: userDetails by email failed (non-blocking)", {
            email,
            error: userDetailsError,
          });
        }
      }

      // Dispatch success action (reducer will set verified=true)
      yield put({ 
        type: VERIFY_OTP_SUCCESS,
        payload: response,
      });
    }
  } catch (err: unknown) {
    const error = err as ErrorResponse;
    yield put({
      type: VERIFY_OTP_FAILURE,
      payload: error.response?.data?.message || "Network error",
    });
  }
}

export function* handleResendOtp(
  action: ResendOtpValidationAction,
): Generator<unknown, void, BaseResponse> {
  try {
    // Ensure payload is an object with emailOrPhone key
    const payload = { emailOrPhone: action.payload.emailOrPhone };
    console.log("🔵 handleResendOtp: Calling resendOtp with payload", {
      actionPayload: action.payload,
      payload,
      payloadType: typeof payload,
      payloadKeys: Object.keys(payload),
    });
    const response: BaseResponse = yield call(resendOtp, payload);

    if (response.status === API_CODES.FAILURE_CODE || response.status === "FAILURE") {
      yield put({
        type: VERIFY_OTP_FAILURE,
        payload: response.statusinfo.message || "Invalid OTP",
      });
    } else {
      yield put({ type: RESEND_OTP_SUCCESS });
    }
  } catch (err: unknown) {
    const error = err as ErrorResponse;
    yield put({
      type: VERIFY_OTP_FAILURE,
      payload: error.response?.data?.message || "Network error",
    });
  }
}

export function* watchVerifyOtp(): Generator {
  yield takeLatest(VERIFY_OTP_REQUEST, handleVerifyOtp);
}

export function* watchResendOtp(): Generator {
  yield takeLatest(RESEND_OTP_REQUEST, handleResendOtp);
}

